import * as React from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  Link,
  IconButton,
  LinearProgress,
  Paper,
  Slider,
  Stack,
  Typography,
} from "@mui/material";
import AnalyticsOutlinedIcon from "@mui/icons-material/AnalyticsOutlined";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import TrendingUpOutlinedIcon from "@mui/icons-material/TrendingUpOutlined";
import ZoomInOutlinedIcon from "@mui/icons-material/ZoomInOutlined";
import ZoomOutOutlinedIcon from "@mui/icons-material/ZoomOutOutlined";
import { supabase } from "../logic/client";
import {
  AVAILABILITY_QUESTION_CODES,
  AvailabilitySignal,
  availabilityPercentFromRaw,
  classifyAvailabilityPercent,
  classifyAvailabilityRaw,
  representativeAvailabilityPercent,
} from "../logic/labAvailability";
import { useI18n } from "../i18n/I18nProvider";

type ScoreBand = "Exposed" | "Developing" | "Operationally Ready";
type LabCategory = "aware" | "prepare";

type LabScoreRow = {
  workshop_id: string;
  employee_id: string;
  first_name: string | null;
  last_name: string | null;
  department: string | null;
  employee_position: string | null;
  aware_points: number | string | null;
  prepare_points: number | string | null;
  lab_score: number | string | null;
  start_score: number | string | null;
  final_score: number | string | null;
  score_delta: number | string | null;
  score_band: ScoreBand | null;
  availability_signal: AvailabilitySignal | null;
  top_strength: string | null;
  top_gap: string | null;
  next_action: string | null;
  updated_at: string | null;
};

type LabQuestionRow = {
  id: string;
  category: LabCategory | string | null;
  question_code: string | null;
  question_en: string | null;
  question_de: string | null;
};

type LabAnswerRow = {
  employee_id: string;
  question_id: string;
  weight: number | string | null;
};

type RiskEntry = {
  code: string;
  label: string;
  count: number;
  answered: number;
  avgWeight: number;
};

type DepartmentReadiness = {
  department: string;
  labScore: number;
  availability: AvailabilitySignal | "No data";
  availabilityPercent: number;
  status: "Ready to scale" | "Needs review" | "Action required";
  action: "View" | "Workshop" | "Escalate";
};

const PREPARE_LABELS: Record<string, { en: string; de: string }> = {
  M1: { en: "72-hour basics", de: "72-Stunden-Grundlagen" },
  M2: { en: "Emergency backpack", de: "Notfallrucksack" },
  M3: { en: "Critical documents", de: "Wichtige Dokumente" },
  M4: { en: "Evacuation route", de: "Evakuierungsroute" },
  M5: { en: "First aid actions", de: "Erste-Hilfe-Handlungen" },
  M6: { en: "Offline communication", de: "Offline-Kommunikation" },
  M7: { en: "Decision routine", de: "Entscheidungsroutine" },
  M8: { en: "Physical evacuation capacity", de: "Körperliche Evakuierungsfähigkeit" },
};

const ACTION_LABELS: Record<string, { en: string; de: string }> = {
  M1: { en: "72-hour supply sprint", de: "72-Stunden-Vorrats-Sprint" },
  M2: { en: "Emergency bag pack-out", de: "Notfallrucksack-Packout" },
  M3: { en: "Document bag delivery", de: "Dokumentenmappe bereitstellen" },
  M4: { en: "Evacuation route plan", de: "Evakuierungsroute planen" },
  M5: { en: "First-aid training block", de: "Erste-Hilfe-Trainingsblock" },
  M6: { en: "Communication plan rollout", de: "Kommunikationsplan ausrollen" },
  M7: { en: "Decision routine drill", de: "Entscheidungsroutine trainieren" },
  M8: { en: "Evacuation capacity check", de: "Evakuierungsfähigkeit prüfen" },
};

const NEXT_ACTIONS: Record<string, { en: string; de: string }> = {
  M1: {
    en: "Close missing water, food, light and medication basics.",
    de: "Fehlende Wasser-, Nahrungs-, Licht- und Medikamenten-Basics schließen.",
  },
  M2: {
    en: "Pack a grab-ready emergency backpack in a fixed place.",
    de: "Einen griffbereiten Notfallrucksack an einem festen Ort packen.",
  },
  M3: {
    en: "Collect key documents in one folder with a secure backup.",
    de: "Wichtige Dokumente in einer Mappe mit sicherem Backup sammeln.",
  },
  M4: {
    en: "Set first and backup destinations with a no-transit route.",
    de: "Erstes Ziel, Ausweichziel und Route ohne ÖPNV festlegen.",
  },
  M5: {
    en: "Refresh one practical emergency action with hands-on training.",
    de: "Eine praktische Notfallhandlung im Training auffrischen.",
  },
  M6: {
    en: "Create offline contact cards and official channel lists.",
    de: "Offline-Kontaktkarten und offizielle Kanallisten erstellen.",
  },
  M7: {
    en: "Practice pause, fact-check and next-safe-action decisions.",
    de: "Innehalten, Faktencheck und nächste sichere Handlung üben.",
  },
  M8: {
    en: "Test a realistic route with the load or support need expected.",
    de: "Eine realistische Route mit Gepäck oder Unterstützungsbedarf testen.",
  },
};

function num(value: number | string | null | undefined) {
  const parsed = typeof value === "number" ? value : Number(value ?? 0);
  return Number.isFinite(parsed) ? parsed : 0;
}

function avg(values: number[]) {
  if (!values.length) return 0;
  return values.reduce((sum, value) => sum + value, 0) / values.length;
}

function pct(part: number, total: number) {
  if (!total) return 0;
  return (part / total) * 100;
}

function clampPct(value: number) {
  return Math.min(100, Math.max(0, Number.isFinite(value) ? value : 0));
}

function formatNumber(value: number, locale: string, digits = 1) {
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(Number.isFinite(value) ? value : 0);
}

function countValues<T extends string>(values: T[]) {
  return values.reduce<Record<string, number>>((acc, value) => {
    acc[value] = (acc[value] ?? 0) + 1;
    return acc;
  }, {});
}

function bandColor(score: number) {
  if (score > 70) return "#20c573";
  if (score > 40) return "#f59e0b";
  return "#f04444";
}

function availabilityColor(signal: AvailabilitySignal | "No data") {
  if (signal === "High") return "#20c573";
  if (signal === "Medium") return "#facc15";
  if (signal === "Low") return "#f04444";
  return "rgba(255,255,255,0.42)";
}

function availabilitySignalForRow(
  row: LabScoreRow,
  availabilityWeightsByEmployee: Map<string, number>,
  answeredAvailabilityCodesByEmployee: Map<string, Set<string>>,
  requiredAvailabilityAnswers: number
) {
  const hasCompleteRawAvailability =
    requiredAvailabilityAnswers > 0 &&
    (answeredAvailabilityCodesByEmployee.get(row.employee_id)?.size ?? 0) >=
      requiredAvailabilityAnswers;

  if (hasCompleteRawAvailability) {
    return classifyAvailabilityRaw(availabilityWeightsByEmployee.get(row.employee_id) ?? 0);
  }

  return row.updated_at && row.availability_signal ? row.availability_signal : null;
}

function availabilityPercentForRow(
  row: LabScoreRow,
  availabilityWeightsByEmployee: Map<string, number>,
  answeredAvailabilityCodesByEmployee: Map<string, Set<string>>,
  requiredAvailabilityAnswers: number
) {
  const hasCompleteRawAvailability =
    requiredAvailabilityAnswers > 0 &&
    (answeredAvailabilityCodesByEmployee.get(row.employee_id)?.size ?? 0) >=
      requiredAvailabilityAnswers;

  if (hasCompleteRawAvailability) {
    return availabilityPercentFromRaw(availabilityWeightsByEmployee.get(row.employee_id) ?? 0);
  }

  return row.updated_at && row.availability_signal
    ? representativeAvailabilityPercent(row.availability_signal)
    : null;
}

function availabilityDistributionGradient(
  counts: Record<string, number>,
  total: number
) {
  if (total <= 0) return "conic-gradient(rgba(255,255,255,0.12) 0 100%)";

  const segments = (["High", "Medium", "Low"] as AvailabilitySignal[]).reduce<{
    start: number;
    parts: string[];
  }>(
    (acc, signal) => {
      const share = pct(counts[signal] ?? 0, total);
      const end = acc.start + share;

      if (share > 0) {
        acc.parts.push(`${availabilityColor(signal)} ${acc.start}% ${end}%`);
      }

      return { start: end, parts: acc.parts };
    },
    { start: 0, parts: [] }
  );

  return `conic-gradient(${segments.parts.join(", ")})`;
}

export default function GroupDashboard(props: { handlePageChange: (page: string) => void }) {
  const { handlePageChange } = props;
  const { lang } = useI18n();
  const isDE = lang === "de";
  const locale = isDE ? "de-DE" : "en-US";
  const L = React.useCallback((en: string, de: string) => (isDE ? de : en), [isDE]);
  const platformViewImages = React.useMemo(
    () =>
      [1, 2].map((page) => ({
        page,
        src: `${process.env.PUBLIC_URL}/platformview${isDE ? "DE" : "EN"}_${page}.png`,
      })),
    [isDE]
  );

  const [rows, setRows] = React.useState<LabScoreRow[]>([]);
  const [questionRows, setQuestionRows] = React.useState<LabQuestionRow[]>([]);
  const [answerRows, setAnswerRows] = React.useState<LabAnswerRow[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [showScoreBreakdown, setShowScoreBreakdown] = React.useState(false);
  const [platformViewOpen, setPlatformViewOpen] = React.useState(false);
  const [platformViewZoom, setPlatformViewZoom] = React.useState(100);
  const [platformViewPage, setPlatformViewPage] = React.useState(0);
  const platformViewSrc = platformViewImages[platformViewPage]?.src ?? platformViewImages[0].src;

  React.useEffect(() => {
    let mounted = true;

    async function load() {
      setLoading(true);
      setError(null);

      const { data, error: rpcError } = await supabase.rpc(
        "get_current_workshop_lab_dashboard"
      );

      if (!mounted) return;

      if (rpcError) {
        console.error("Failed to load current workshop lab dashboard:", rpcError);
        setRows([]);
        setQuestionRows([]);
        setAnswerRows([]);
        setError(
          L(
            "Could not load current workshop score aggregates from the database.",
            "Die Score-Aggregate des aktuellen Workshops konnten nicht aus der Datenbank geladen werden."
          )
        );
      } else {
        const dashboardRows = (data ?? []) as LabScoreRow[];
        setRows(dashboardRows);

        const employeeIds = dashboardRows
          .map((row) => row.employee_id)
          .filter((employeeId): employeeId is string => Boolean(employeeId));

        if (employeeIds.length === 0) {
          setQuestionRows([]);
          setAnswerRows([]);
        } else {
          const [questionResult, answerResult] = await Promise.all([
            supabase
              .from("vibe_questions_v2")
              .select("id, category, question_code, question_en, question_de")
              .in("category", ["aware", "prepare"])
              .order("order_index", { ascending: true }),
            supabase
              .from("employee_vibe_answers_v2")
              .select("employee_id, question_id, weight")
              .in("employee_id", employeeIds),
          ]);

          if (!mounted) return;

          if (questionResult.error || answerResult.error) {
            console.error("Failed to load workshop completion data:", {
              questionError: questionResult.error,
              answerError: answerResult.error,
            });
            setQuestionRows([]);
            setAnswerRows([]);
            setError(
              L(
                "Scores loaded, but completion and risk details could not be loaded.",
                "Scores geladen, aber Abschluss- und Risikodetails konnten nicht geladen werden."
              )
            );
          } else {
            setQuestionRows((questionResult.data ?? []) as LabQuestionRow[]);
            setAnswerRows((answerResult.data ?? []) as LabAnswerRow[]);
          }
        }
      }

      setLoading(false);
    }

    load();

    return () => {
      mounted = false;
    };
  }, [L]);

  const participantCount = rows.length;
  const labScores = rows.map((row) => num(row.final_score ?? row.lab_score));
  const awareScores = rows.map((row) => num(row.aware_points));
  const prepareScores = rows.map((row) => num(row.prepare_points));
  const overallAvg = avg(labScores);
  const awareAvg = avg(awareScores);
  const prepareAvg = avg(prepareScores);
  const questionById = new Map(questionRows.map((question) => [question.id, question]));
  const labQuestionIds = new Set(
    questionRows
      .filter((question) => question.category === "aware" || question.category === "prepare")
      .map((question) => question.id)
  );
  const availabilityQuestionCodes = new Set<string>(AVAILABILITY_QUESTION_CODES);
  const participantIds = rows.map((row) => row.employee_id);
  const answeredLabQuestionsByEmployee = new Map<string, Set<string>>();
  const availabilityWeightsByEmployee = new Map<string, number>();
  const availabilityWeightsByEmployeeCode = new Map<string, Map<string, number>>();
  const answeredAvailabilityCodesByEmployee = new Map<string, Set<string>>();

  answerRows.forEach((answer) => {
    if (!labQuestionIds.has(answer.question_id)) return;

    const answeredSet = answeredLabQuestionsByEmployee.get(answer.employee_id) ?? new Set<string>();
    answeredSet.add(answer.question_id);
    answeredLabQuestionsByEmployee.set(answer.employee_id, answeredSet);

    const question = questionById.get(answer.question_id);
    const code = question?.question_code ?? "";

    if (availabilityQuestionCodes.has(code)) {
      const answeredAvailabilityCodes =
        answeredAvailabilityCodesByEmployee.get(answer.employee_id) ?? new Set<string>();
      answeredAvailabilityCodes.add(code);
      answeredAvailabilityCodesByEmployee.set(answer.employee_id, answeredAvailabilityCodes);

      const weightsByCode =
        availabilityWeightsByEmployeeCode.get(answer.employee_id) ?? new Map<string, number>();
      const weight = num(answer.weight);
      weightsByCode.set(code, Math.max(weightsByCode.get(code) ?? 0, weight));
      availabilityWeightsByEmployeeCode.set(answer.employee_id, weightsByCode);
    }
  });

  availabilityWeightsByEmployeeCode.forEach((weightsByCode, employeeId) => {
    availabilityWeightsByEmployee.set(
      employeeId,
      Array.from(weightsByCode.values()).reduce((sum, weight) => sum + weight, 0)
    );
  });

  const completedParticipants = participantIds.filter(
    (employeeId) =>
      labQuestionIds.size > 0 &&
      (answeredLabQuestionsByEmployee.get(employeeId)?.size ?? 0) >= labQuestionIds.size
  ).length;
  const completionPct = pct(completedParticipants, participantCount);
  const availabilitySignals = rows
    .map((row) =>
      availabilitySignalForRow(
        row,
        availabilityWeightsByEmployee,
        answeredAvailabilityCodesByEmployee,
        AVAILABILITY_QUESTION_CODES.length
      )
    )
    .filter((signal): signal is AvailabilitySignal => Boolean(signal));
  const availabilityPercents = rows
    .map((row) =>
      availabilityPercentForRow(
        row,
        availabilityWeightsByEmployee,
        answeredAvailabilityCodesByEmployee,
        AVAILABILITY_QUESTION_CODES.length
      )
    )
    .filter((percent): percent is number => percent !== null);
  const respondentAvailabilityCounts = countValues(availabilitySignals);
  const workforceAvailabilityScore = availabilityPercents.length
    ? avg(availabilityPercents)
    : 0;
  const workforceAvailabilitySignal = classifyAvailabilityPercent(workforceAvailabilityScore);
  const prepareRiskMap = new Map<string, { question: LabQuestionRow; weights: number[]; lowCount: number }>();

  questionRows
    .filter((question) => question.category === "prepare" && question.question_code)
    .forEach((question) => {
      prepareRiskMap.set(question.question_code ?? "", {
        question,
        weights: [],
        lowCount: 0,
      });
    });

  answerRows.forEach((answer) => {
    const question = questionById.get(answer.question_id);
    const code = question?.question_code ?? "";
    const entry = prepareRiskMap.get(code);

    if (!entry) return;

    const weight = num(answer.weight);
    entry.weights.push(weight);
    if (weight <= 1) entry.lowCount += 1;
  });

  const riskEntries: RiskEntry[] = Array.from(prepareRiskMap.entries())
    .map(([code, entry]) => ({
      code,
      label:
        (isDE ? PREPARE_LABELS[code]?.de : PREPARE_LABELS[code]?.en) ||
        (isDE ? entry.question.question_de : entry.question.question_en) ||
        code,
      count: entry.lowCount,
      answered: entry.weights.length,
      avgWeight: avg(entry.weights),
    }))
    .filter((entry) => entry.count > 0)
    .sort((a, b) => {
      const countDiff = b.count - a.count;
      if (countDiff !== 0) return countDiff;
      return a.avgWeight - b.avgWeight;
    })
    .slice(0, 5);
  const departmentRows = buildDepartmentReadiness(
    rows,
    availabilityWeightsByEmployee,
    answeredAvailabilityCodesByEmployee,
    AVAILABILITY_QUESTION_CODES.length
  );

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "grid",
          placeItems: "center",
          background: controlPanelBackground,
        }}
      >
        <Stack spacing={2} alignItems="center">
          <CircularProgress sx={{ color: "#ff3d4d" }} />
          <Typography sx={{ color: "rgba(255,255,255,0.78)", fontWeight: 800 }}>
            {L("Loading current workshop averages...", "Lade Durchschnittswerte des aktuellen Workshops...")}
          </Typography>
        </Stack>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: "100vh", background: controlPanelBackground, color: "#fff", position: "relative" }}>
      {/* BACK BUTTON */}
      <Box sx={{ position: "absolute", top: { xs: 12, md: 24 }, left: { xs: 12, md: 24 }, zIndex: 10 }}>
        <IconButton
          onClick={() => handlePageChange("ControlPanel")}
          sx={{
            color: "white",
            background: "rgba(255,255,255,0.06)",
            backdropFilter: "blur(6px)",
            border: "1px solid rgba(255,255,255,0.1)",
            boxShadow: "0 0 15px rgba(255,255,255,0.1)",
            "&:hover": { background: "rgba(255,255,255,0.12)" },
          }}
        >
          <HomeOutlinedIcon />
        </IconButton>
      </Box>

      <Box sx={{ maxWidth: 1400, mx: "auto", p: { xs: 2, md: 4 }, pt: { xs: 8, md: 4 } }}>
        <Stack spacing={3}>
          <Paper sx={glassCard}>
            <Stack direction={{ xs: "column", md: "row" }} spacing={2} justifyContent="space-between">
              <Stack spacing={1}>
                <Stack direction="row" spacing={1.25} alignItems="center">
                  <AnalyticsOutlinedIcon sx={{ color: "#ff3d4d" }} />
                  <Typography sx={{ fontSize: { xs: 26, md: 34 }, fontWeight: 950, lineHeight: 1.1 }}>
                    {L("Manager dashboard", "Manager-Dashboard")}
                  </Typography>
                </Stack>

                <Typography sx={{ maxWidth: 900, color: "rgba(255,255,255,0.72)", lineHeight: 1.55 }}>
                  {L(
                    "Current workshop participant averages only.",
                    "Nur Durchschnittswerte der Teilnehmenden des aktuellen Workshops."
                  )}
                </Typography>

                {error && (
                  <Typography sx={{ color: "#ff8a8a", fontWeight: 800 }}>
                    {error}
                  </Typography>
                )}
              </Stack>

              <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: "wrap", rowGap: 1 }}>
                <Button
                  onClick={() => setPlatformViewOpen(true)}
                  endIcon={<ImageOutlinedIcon />}
                  sx={{
                    color: "#fff",
                    fontWeight: 900,
                    textTransform: "none",
                    borderRadius: 999,
                    px: 1.45,
                    py: 0.8,
                    bgcolor: "rgba(255,255,255,0.06)",
                    border: "1px solid rgba(255,255,255,0.1)",
                    "&:hover": { bgcolor: "rgba(255,255,255,0.12)" },
                  }}
                >
                  {L("Platform view", "Plattformansicht")}
                </Button>
                <InfoPill label={L("Participants", "Teilnehmende")} value={String(participantCount)} />
              </Stack>
            </Stack>
          </Paper>

          {participantCount === 0 ? (
            <Paper sx={glassCard}>
              <Typography sx={{ fontWeight: 950, fontSize: 22, mb: 1 }}>
                {L("No current workshop score data yet", "Noch keine Score-Daten für den aktuellen Workshop")}
              </Typography>
              <Typography sx={{ color: "rgba(255,255,255,0.72)" }}>
                {L(
                  "Once participants complete AWARE and PREPARE, their scores will appear here as aggregated averages.",
                  "Sobald Teilnehmende AWARE und PREPARE abschließen, erscheinen ihre Scores hier als aggregierte Durchschnittswerte."
                )}
              </Typography>
            </Paper>
          ) : (
            <>
              <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                <AvailabilityGaugeCard
                  label={L("Workforce availability", "Workforce-Verfügbarkeit")}
                  value={workforceAvailabilitySignal}
                  counts={respondentAvailabilityCounts}
                  total={availabilitySignals.length}
                  participantTotal={participantCount}
                  locale={locale}
                  noDataLabel={L("No respondent data yet", "Noch keine Antwortdaten")}
                  respondentLabel={L(
                    "respondents with availability answers",
                    "Teilnehmende mit Verfügbarkeitsantworten"
                  )}
                  signalLabels={{
                    High: L("High", "Hoch"),
                    Medium: L("Medium", "Mittel"),
                    Low: L("Low", "Niedrig"),
                  }}
                />

                <MetricCard
                  icon={<TrendingUpOutlinedIcon />}
                  label={L("Average Lab Score", "Durchschnittlicher Lab Score")}
                  value={
                    showScoreBreakdown
                      ? `${formatNumber(awareAvg, locale)}/60 · ${formatNumber(prepareAvg, locale)}/40`
                      : `${formatNumber(overallAvg, locale)}/100`
                  }
                  progress={overallAvg}
                  helper={
                    showScoreBreakdown
                      ? L("Average AWARE and PREPARE score contribution.", "Durchschnittlicher BEWUSST- und BEREIT-Score-Beitrag.")
                      : L("Average final score of current workshop participants.", "Durchschnittlicher Final Score der aktuellen Workshop-Teilnehmenden.")
                  }
                  actionLabel={
                    showScoreBreakdown
                      ? L("Show total score", "Gesamtscore anzeigen")
                      : L("Click for AWARE/PREPARE breakdown -->", "Für BEWUSST/BEREIT-Aufschlüsselung klicken -->")
                  }
                  onAction={() => setShowScoreBreakdown((value) => !value)}
                />

                <MetricCard
                  icon={<ShieldOutlinedIcon />}
                  label={L("Assessment completion", "Assessment-Abschluss")}
                  value={`${formatNumber(completionPct, locale, 0)}%`}
                  progress={completionPct}
                  progressColor="#3b82f6"
                  helper={L(
                    `${completedParticipants} out of ${participantCount} completed`,
                    `${completedParticipants} von ${participantCount} abgeschlossen`
                  )}
                />
              </Stack>

              <Stack direction={{ xs: "column", lg: "row" }} spacing={2}>
                <Panel title={L("Top company risks", "Top-Unternehmensrisiken")}>
                  {riskEntries.length === 0 ? (
                    <Typography sx={mutedText}>
                      {L("No low-scoring PREPARE risks found yet.", "Noch keine niedrig bewerteten PREPARE-Risiken gefunden.")}
                    </Typography>
                  ) : (
                    <Stack spacing={1}>
                      {riskEntries.slice(0, 3).map((risk, index) => (
                        <RiskRow
                          key={risk.code}
                          risk={risk}
                          index={index}
                          total={participantCount}
                          locale={locale}
                          staffLabel={L("staff below target", "Mitarbeitende unter Zielwert")}
                        />
                      ))}
                    </Stack>
                  )}
                </Panel>

                <Panel title={L("Recommended actions — ranked by impact", "Empfohlene Maßnahmen — nach Wirkung sortiert")}>
                  {riskEntries.length === 0 ? (
                    <Typography sx={mutedText}>
                      {L("No action recommendations needed from current PREPARE answers.", "Aus den aktuellen PREPARE-Antworten sind keine Maßnahmen nötig.")}
                    </Typography>
                  ) : (
                    <Stack spacing={1}>
                      {riskEntries.slice(0, 3).map((risk) => (
                        <ActionRow
                          key={risk.code}
                          risk={risk}
                          total={participantCount}
                          locale={locale}
                          isDE={isDE}
                        />
                      ))}
                    </Stack>
                  )}
                </Panel>
              </Stack>

              <Panel title={L("Department readiness", "Abteilungs-Readiness")}>
                <DepartmentReadinessTable
                  rows={departmentRows}
                  locale={locale}
                  labels={{
                    note: L("Individual scores are not shown", "Individuelle Scores werden nicht angezeigt"),
                    department: L("Department", "Abteilung"),
                    labScore: L("Lab score", "Lab Score"),
                    availability: L("Availability", "Verfügbarkeit"),
                    status: L("Status", "Status"),
                    action: L("Action", "Aktion"),
                    noData: L("No department data available yet.", "Noch keine Abteilungsdaten verfügbar."),
                  }}
                />
              </Panel>
            </>
          )}
        </Stack>
      </Box>

      <Dialog
        open={platformViewOpen}
        onClose={() => setPlatformViewOpen(false)}
        fullWidth
        maxWidth="lg"
        PaperProps={{
          sx: {
            height: "90vh",
            bgcolor: "#111010",
            color: "#fff",
            borderRadius: 3,
            border: "1px solid rgba(255,255,255,0.12)",
            overflow: "hidden",
          },
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.5}
          alignItems={{ xs: "stretch", sm: "center" }}
          justifyContent="space-between"
          sx={{
            p: 1.5,
            borderBottom: "1px solid rgba(255,255,255,0.1)",
            background: "rgba(255,255,255,0.04)",
          }}
        >
          <Typography sx={{ fontWeight: 950 }}>
            {L("Platform view", "Plattformansicht")}
          </Typography>

          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Stack
              direction="row"
              spacing={0.5}
              alignItems="center"
              sx={{
                p: 0.35,
                borderRadius: 999,
                bgcolor: "rgba(255,255,255,0.06)",
                border: "1px solid rgba(255,255,255,0.1)",
              }}
            >
              {platformViewImages.map((image, index) => {
                const selected = index === platformViewPage;

                return (
                  <Button
                    key={image.page}
                    size="small"
                    onClick={() => setPlatformViewPage(index)}
                    aria-pressed={selected}
                    sx={{
                      minWidth: 34,
                      height: 30,
                      borderRadius: 999,
                      color: selected ? "#111010" : "#fff",
                      bgcolor: selected ? "#fff" : "transparent",
                      fontWeight: 950,
                      lineHeight: 1,
                      "&:hover": {
                        bgcolor: selected ? "#fff" : "rgba(255,255,255,0.12)",
                      },
                    }}
                  >
                    {image.page}
                  </Button>
                );
              })}
            </Stack>

            <IconButton
              onClick={() => setPlatformViewZoom((zoom) => Math.max(100, zoom - 25))}
              disabled={platformViewZoom <= 100}
              sx={platformViewIconButtonSx}
            >
              <ZoomOutOutlinedIcon />
            </IconButton>
            <Slider
              min={100}
              max={250}
              step={25}
              value={platformViewZoom}
              onChange={(_event, value) =>
                setPlatformViewZoom(Array.isArray(value) ? value[0] : value)
              }
              aria-label={L("Image zoom", "Bild-Zoom")}
              sx={{
                width: { xs: 120, sm: 180 },
                color: "#ff3d4d",
                "& .MuiSlider-thumb": { boxShadow: "0 0 0 6px rgba(255,61,77,0.16)" },
              }}
            />
            <Typography sx={{ width: 48, textAlign: "right", fontWeight: 900 }}>
              {platformViewZoom}%
            </Typography>
            <IconButton
              onClick={() => setPlatformViewZoom((zoom) => Math.min(250, zoom + 25))}
              disabled={platformViewZoom >= 250}
              sx={platformViewIconButtonSx}
            >
              <ZoomInOutlinedIcon />
            </IconButton>
            <IconButton
              onClick={() => setPlatformViewOpen(false)}
              sx={platformViewIconButtonSx}
            >
              <CloseOutlinedIcon />
            </IconButton>
          </Stack>
        </Stack>

        <DialogContent
          sx={{
            p: 0,
            overflow: "auto",
            bgcolor: "rgba(0,0,0,0.36)",
          }}
        >
          <Box
            component="img"
            src={platformViewSrc}
            alt={`${L("Platform view", "Plattformansicht")} ${platformViewPage + 1}`}
            sx={{
              display: "block",
              width: `${platformViewZoom}%`,
              minWidth: "100%",
              height: "auto",
              mx: "auto",
            }}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
}

const controlPanelBackground = `
  radial-gradient(circle at 20% 20%, rgba(251, 2, 2, 0.08), transparent 40%),
  radial-gradient(circle at 80% 30%, rgba(255, 0, 0, 0.06), transparent 40%),
  #111010
`;

const glassCard = {
  borderRadius: 5,
  p: { xs: 2.5, md: 3 },
  background:
    "linear-gradient(135deg, rgba(255,255,255,0.06), rgba(255,255,255,0.025))",
  backdropFilter: "blur(14px)",
  border: "1px solid rgba(255,255,255,0.08)",
  color: "white",
};

const mutedText = {
  color: "rgba(255,255,255,0.68)",
  fontWeight: 700,
};

const platformViewIconButtonSx = {
  color: "white",
  bgcolor: "rgba(255,255,255,0.06)",
  border: "1px solid rgba(255,255,255,0.1)",
  "&:hover": { bgcolor: "rgba(255,255,255,0.12)" },
  "&.Mui-disabled": {
    color: "rgba(255,255,255,0.28)",
    borderColor: "rgba(255,255,255,0.06)",
  },
};

function InfoPill(props: { label: string; value: string }) {
  return (
    <Box
      sx={{
        px: 1.35,
        py: 0.85,
        borderRadius: 999,
        bgcolor: "rgba(255,255,255,0.06)",
        border: "1px solid rgba(255,255,255,0.1)",
      }}
    >
      <Typography component="span" sx={{ color: "rgba(255,255,255,0.54)", fontWeight: 900, mr: 0.75 }}>
        {props.label}
      </Typography>
      <Typography component="span" sx={{ color: "#fff", fontWeight: 900 }}>
        {props.value}
      </Typography>
    </Box>
  );
}

function MetricCard(props: {
  icon: React.ReactNode;
  label: string;
  value: string;
  progress: number;
  helper: string;
  progressColor?: string;
  actionLabel?: string;
  onAction?: () => void;
}) {
  return (
    <Card sx={{ flex: 1, ...glassCard }}>
      <CardContent sx={{ p: 0, "&:last-child": { pb: 0 } }}>
        <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 2 }}>
          <Box sx={{ color: "#ff3d4d", display: "grid", placeItems: "center" }}>
            {props.icon}
          </Box>
          <Typography sx={{ color: "rgba(255,255,255,0.68)", fontWeight: 900 }}>
            {props.label}
          </Typography>
        </Stack>

        <Typography sx={{ fontSize: { xs: 34, md: 42 }, fontWeight: 950, lineHeight: 1 }}>
          {props.value}
        </Typography>

        <LinearProgress
          variant="determinate"
          value={clampPct(props.progress)}
          sx={{
            mt: 2,
            height: 10,
            borderRadius: 99,
            bgcolor: "rgba(255,255,255,0.13)",
            "& .MuiLinearProgress-bar": {
              borderRadius: 99,
              bgcolor: props.progressColor ?? bandColor(props.progress),
            },
          }}
        />

        <Typography sx={{ color: "rgba(255,255,255,0.58)", fontSize: 13, fontWeight: 700, mt: 1.5 }}>
          {props.helper}
        </Typography>

        {props.actionLabel && props.onAction && (
          <Link
            component="button"
            type="button"
            onClick={props.onAction}
            sx={{
              color: "#93c5fd",
              fontSize: 13,
              fontWeight: 900,
              mt: 1.25,
              textAlign: "left",
              textDecorationColor: "rgba(147,197,253,0.42)",
              "&:hover": { color: "#bfdbfe" },
            }}
          >
            {props.actionLabel}
          </Link>
        )}
      </CardContent>
    </Card>
  );
}

function AvailabilityGaugeCard(props: {
  label: string;
  value: AvailabilitySignal;
  counts: Record<string, number>;
  total: number;
  participantTotal: number;
  locale: string;
  noDataLabel: string;
  respondentLabel: string;
  signalLabels: Record<AvailabilitySignal, string>;
}) {
  const color = availabilityColor(props.total > 0 ? props.value : "No data");
  const valueLabel = props.total > 0 ? props.signalLabels[props.value] : props.noDataLabel;
  const gaugeBackground = availabilityDistributionGradient(props.counts, props.total);
  const highCount = props.counts.High ?? 0;

  return (
    <Card sx={{ flex: 1, ...glassCard }}>
      <CardContent sx={{ p: 0, "&:last-child": { pb: 0 } }}>
        <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 2 }}>
          <Box sx={{ color, display: "grid", placeItems: "center" }}>
            <GroupsOutlinedIcon />
          </Box>
          <Typography sx={{ color: "rgba(255,255,255,0.68)", fontWeight: 900 }}>
            {props.label}
          </Typography>
        </Stack>

        <Stack direction="row" spacing={2} alignItems="center">
          <Box
            sx={{
              width: 104,
              height: 104,
              borderRadius: "50%",
              background: gaugeBackground,
              display: "grid",
              placeItems: "center",
              flex: "0 0 auto",
            }}
          >
            <Box
              sx={{
                width: 76,
                height: 76,
                borderRadius: "50%",
                bgcolor: "rgba(17,16,16,0.96)",
                display: "grid",
                placeItems: "center",
                border: "1px solid rgba(255,255,255,0.08)",
              }}
            >
              <Typography sx={{ color, fontWeight: 950, fontSize: 22 }}>
                {props.participantTotal > 0 ? `${highCount}/${props.participantTotal}` : "--"}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography sx={{ fontSize: { xs: 30, md: 38 }, fontWeight: 950, lineHeight: 1 }}>
              {valueLabel}
            </Typography>
            <Typography sx={{ color: "rgba(255,255,255,0.58)", fontSize: 13, fontWeight: 700, mt: 1 }}>
              {props.total} {props.respondentLabel}
            </Typography>
          </Box>
        </Stack>

        <Stack direction="row" spacing={1} sx={{ mt: 2, flexWrap: "wrap", rowGap: 1 }}>
          {(["High", "Medium", "Low"] as AvailabilitySignal[]).map((signal) => (
            <Chip
              key={signal}
              label={`${props.signalLabels[signal]} ${props.counts[signal] ?? 0}`}
              size="small"
              sx={{
                bgcolor: `${availabilityColor(signal)}24`,
                color: availabilityColor(signal),
                border: `1px solid ${availabilityColor(signal)}55`,
                fontWeight: 950,
              }}
            />
          ))}
        </Stack>
      </CardContent>
    </Card>
  );
}

function Panel(props: { title: string; children: React.ReactNode }) {
  return (
    <Paper sx={{ ...glassCard, flex: 1 }}>
      <Typography sx={{ fontWeight: 950, mb: 2, textTransform: "uppercase", letterSpacing: 0, color: "rgba(255,255,255,0.68)", fontSize: 13 }}>
        {props.title}
      </Typography>
      {props.children}
    </Paper>
  );
}

function RiskRow(props: {
  risk: RiskEntry;
  index: number;
  total: number;
  locale: string;
  staffLabel: string;
}) {
  const colors = ["#f04444", "#f59e0b", "#3b82f6"];
  const color = colors[props.index] ?? "#3b82f6";
  const affectedPct = pct(props.risk.count, props.total);

  return (
    <Box
      sx={{
        p: 1.25,
        borderRadius: 2,
        bgcolor: "rgba(10,19,38,0.66)",
        borderLeft: `4px solid ${color}`,
      }}
    >
      <Stack direction="row" justifyContent="space-between" spacing={2} alignItems="flex-start">
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ color: "#fff", fontWeight: 950, lineHeight: 1.25 }}>
            {props.risk.code} · {props.risk.label}
          </Typography>
          <Typography sx={{ color: "rgba(255,255,255,0.58)", fontSize: 12, fontWeight: 700, mt: 0.35 }}>
            {props.risk.count} {props.staffLabel} · {formatNumber(affectedPct, props.locale, 0)}%
          </Typography>
        </Box>
        <Chip
          label={`${props.risk.count}/${props.risk.answered}`}
          size="small"
          sx={{
            bgcolor: "rgba(255,255,255,0.08)",
            color: "rgba(255,255,255,0.82)",
            fontWeight: 950,
          }}
        />
      </Stack>
    </Box>
  );
}

function ActionRow(props: {
  risk: RiskEntry;
  total: number;
  locale: string;
  isDE: boolean;
}) {
  const impact = pct(props.risk.count, props.total);
  const actionLabel =
    (props.isDE ? ACTION_LABELS[props.risk.code]?.de : ACTION_LABELS[props.risk.code]?.en) ||
    props.risk.label;
  const actionDetail =
    (props.isDE ? NEXT_ACTIONS[props.risk.code]?.de : NEXT_ACTIONS[props.risk.code]?.en) ||
    props.risk.label;

  return (
    <Box
      sx={{
        p: 1.25,
        borderRadius: 2,
        bgcolor: "rgba(10,19,38,0.66)",
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Box
          sx={{
            width: 10,
            height: 10,
            borderRadius: "50%",
            bgcolor: "#3b82f6",
            flex: "0 0 auto",
          }}
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ color: "#fff", fontWeight: 950, lineHeight: 1.25 }}>
            {actionLabel} ({props.risk.code})
          </Typography>
          <Typography sx={{ color: "rgba(255,255,255,0.58)", fontSize: 12, fontWeight: 700, mt: 0.35 }}>
            {actionDetail}
          </Typography>
        </Box>
        <Chip
          label={`+${formatNumber(impact, props.locale, 0)}%`}
          size="small"
          sx={{
            bgcolor: "rgba(59,130,246,0.2)",
            color: "#60a5fa",
            fontWeight: 950,
          }}
        />
      </Stack>
    </Box>
  );
}

function DepartmentReadinessTable(props: {
  rows: DepartmentReadiness[];
  locale: string;
  labels: {
    note: string;
    department: string;
    labScore: string;
    availability: string;
    status: string;
    action: string;
    noData: string;
  };
}) {
  if (props.rows.length === 0) {
    return <Typography sx={mutedText}>{props.labels.noData}</Typography>;
  }

  return (
    <Box sx={{ overflowX: "auto" }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        sx={{ color: "rgba(255,255,255,0.42)", fontSize: 12, fontWeight: 800, mb: 1.5 }}
      >
        <Box />
        <Typography sx={{ color: "rgba(255,255,255,0.42)", fontSize: 12, fontWeight: 800 }}>
          {props.labels.note}
        </Typography>
      </Stack>
      <Box
        sx={{
          minWidth: 760,
          display: "grid",
          gridTemplateColumns: "1.35fr 1.6fr 1fr 1.45fr 0.85fr",
          columnGap: 2,
          rowGap: 1.25,
          alignItems: "center",
        }}
      >
        {[props.labels.department, props.labels.labScore, props.labels.availability, props.labels.status, props.labels.action].map((label) => (
          <Typography
            key={label}
            sx={{
              color: "rgba(255,255,255,0.38)",
              fontSize: 12,
              fontWeight: 950,
              textTransform: "uppercase",
            }}
          >
            {label}
          </Typography>
        ))}

        {props.rows.map((row) => (
          <React.Fragment key={row.department}>
            <Typography sx={{ color: "#fff", fontWeight: 850 }}>{row.department}</Typography>
            <Stack direction="row" spacing={2} alignItems="center">
              <Typography sx={{ color: "#fff", fontWeight: 950, width: 38 }}>
                {formatNumber(row.labScore, props.locale, 0)}
              </Typography>
              <LinearProgress
                variant="determinate"
                value={clampPct(row.labScore)}
                sx={{
                  height: 7,
                  borderRadius: 99,
                  bgcolor: "rgba(148,163,184,0.22)",
                  flex: 1,
                  "& .MuiLinearProgress-bar": {
                    borderRadius: 99,
                    bgcolor: availabilityColor(row.availability),
                  },
                }}
              />
            </Stack>
            <Typography sx={{ color: availabilityColor(row.availability), fontWeight: 950 }}>
              {row.availability}
            </Typography>
            <StatusDot status={row.status} />
            <Chip
              label={row.action}
              size="small"
              sx={{
                bgcolor:
                  row.action === "Escalate"
                    ? "rgba(239,68,68,0.26)"
                    : row.action === "Workshop"
                    ? "rgba(250,204,21,0.2)"
                    : "rgba(148,163,184,0.18)",
                color:
                  row.action === "Escalate"
                    ? "#f87171"
                    : row.action === "Workshop"
                    ? "#fde047"
                    : "rgba(255,255,255,0.72)",
                fontWeight: 950,
              }}
            />
          </React.Fragment>
        ))}
      </Box>
    </Box>
  );
}

function StatusDot(props: { status: DepartmentReadiness["status"] }) {
  const color =
    props.status === "Ready to scale"
      ? "#20c573"
      : props.status === "Needs review"
      ? "#facc15"
      : "#f04444";

  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <Box sx={{ width: 9, height: 9, borderRadius: "50%", bgcolor: color }} />
      <Typography sx={{ color: "rgba(255,255,255,0.82)", fontWeight: 850 }}>
        {props.status}
      </Typography>
    </Stack>
  );
}

function buildDepartmentReadiness(
  rows: LabScoreRow[],
  availabilityWeightsByEmployee: Map<string, number>,
  answeredAvailabilityCodesByEmployee: Map<string, Set<string>>,
  requiredAvailabilityAnswers: number
) {
  const byDepartment = new Map<string, LabScoreRow[]>();

  rows.forEach((row) => {
    const department = row.department?.trim() || "Unassigned";
    byDepartment.set(department, [...(byDepartment.get(department) ?? []), row]);
  });

  return Array.from(byDepartment.entries())
    .map(([department, departmentRows]) => {
      const labScore = avg(departmentRows.map((row) => num(row.final_score ?? row.lab_score)));
      const availabilityPercents = departmentRows
        .map((row) =>
          availabilityPercentForRow(
            row,
            availabilityWeightsByEmployee,
            answeredAvailabilityCodesByEmployee,
            requiredAvailabilityAnswers
          )
        )
        .filter((percent): percent is number => percent !== null);
      const averageAvailability = availabilityPercents.length
        ? avg(availabilityPercents)
        : 0;
      const availability: DepartmentReadiness["availability"] =
        availabilityPercents.length === 0
          ? "No data"
          : classifyAvailabilityPercent(averageAvailability);
      const status: DepartmentReadiness["status"] =
        labScore >= 75 && availability === "High"
          ? "Ready to scale"
          : labScore >= 55 && availability !== "Low" && availability !== "No data"
          ? "Needs review"
          : "Action required";
      const action: DepartmentReadiness["action"] =
        status === "Ready to scale" ? "View" : status === "Needs review" ? "Workshop" : "Escalate";

      return {
        department,
        labScore,
        availability,
        availabilityPercent: averageAvailability,
        status,
        action,
      };
    })
    .sort((a, b) => a.labScore - b.labScore);
}
